import {
  useCallback,
  useEffect,
  useEffectEvent,
  useRef,
  useState,
} from "react";
import { t, useLocale } from "../i18n";

// Document PiP is available in supporting desktop browsers, over HTTPS.
type PictureInPictureWindow = Window & {
  documentPictureInPicture?: {
    window: Window | null;
    requestWindow: (options: {
      width: number;
      height: number;
    }) => Promise<Window>;
  };
};
// The browser action is newer than TypeScript's MediaSessionAction catalogue.
const enterPictureInPicture = "enterpictureinpicture" as MediaSessionAction;

export function useCallPictureInPicture(callId: string | null, video: boolean) {
  const api = (window as PictureInPictureWindow).documentPictureInPicture;
  const [pipWindow, setPipWindow] = useState<Window | null>(null);
  const [failedCall, setFailedCall] = useState<string | null>(null);
  const pending = useRef(false),
    generation = useRef(0);
  const locale = useLocale();

  const open = useCallback(
    async (showError = true) => {
      if (!api || !callId || pending.current || api.window) return;
      pending.current = true;
      setFailedCall(null);
      const token = generation.current;
      try {
        const next = await api.requestWindow({
          width: 320,
          height: video ? 260 : 100,
        });
        // A request finishing after hangup must never leave an empty window behind.
        if (generation.current !== token) {
          next.close();
          return;
        }
        const target = next.document;
        target.title = t("رنّة | على الخط");
        target.documentElement.lang = document.documentElement.lang;
        target.documentElement.dir = document.documentElement.dir;
        target.documentElement.classList.add("call-pip");
        const base = target.createElement("base");
        base.href = document.baseURI;
        target.head.append(base);
        for (const style of document.querySelectorAll(
          'style, link[rel="stylesheet"]',
        ))
          target.head.append(style.cloneNode(true));
        next.addEventListener(
          "pagehide",
          () => setPipWindow((current) => (current === next ? null : current)),
          { once: true },
        );
        setPipWindow(next);
      } catch {
        // Permission can be denied for automatic PiP; the call keeps going.
        if (showError && generation.current === token) setFailedCall(callId);
      } finally {
        pending.current = false;
      }
    },
    [api, callId, video],
  );

  const enter = useEffectEvent(() => {
    void open(false);
  });
  useEffect(() => {
    if (!api || !callId || !navigator.mediaSession) return;
    try {
      // The browser grants activation here when a capturing call leaves the tab.
      navigator.mediaSession.setActionHandler(enterPictureInPicture, enter);
    } catch {
      // Manual PiP can still work without the automatic media-session action.
      return;
    }
    return () => {
      navigator.mediaSession.setActionHandler(enterPictureInPicture, null);
    };
  }, [api, callId]);

  useEffect(() => {
    const restore = () => {
      if (!document.hidden) {
        generation.current++;
        api?.window?.close();
      }
    };
    document.addEventListener("visibilitychange", restore);
    return () => {
      document.removeEventListener("visibilitychange", restore);
      // This ref is a request generation counter, not a rendered DOM node.
      // oxlint-disable-next-line react-hooks/exhaustive-deps
      generation.current++;
      api?.window?.close();
    };
  }, [api, callId]);

  useEffect(() => {
    const target = api?.window?.document;
    if (!target) return;
    target.documentElement.lang = locale;
    target.documentElement.dir = locale === "ar" ? "rtl" : "ltr";
    target.title = t("رنّة | على الخط");
  }, [api, pipWindow, locale]);

  return {
    supported: !!api && !!callId,
    pipWindow,
    failed: !!callId && failedCall === callId,
    open,
    close: () => api?.window?.close(),
  };
}
